import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  open: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  answered: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  closed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const AdminSupport = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();

  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!loading && (!isAuthenticated || user?.role !== 'admin')) {
      navigate('/');
    }
  }, [isAuthenticated, loading, user, navigate]);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') return;
    axios
      .get(`${BACKEND_URL}/api/telegram/support-tickets`)
      .then(({ data }) => setTickets(data.tickets || []))
      .catch((err) => setError(err.response?.data?.detail || 'Could not load tickets.'));
  }, [isAuthenticated, user]);

  if (loading || !isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#06070d] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f2e2a6]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#06070d] via-[#0b0e1a] to-[#06070d]">
      <Navbar />

      <div className="pt-24 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-2">Support tickets</h1>
          <p className="text-gray-400 mb-8">
            Read-only history of every /support ticket — the queue itself lives in the Telegram group.
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm mb-6">
              {error}
            </div>
          )}

          {!tickets ? (
            <div className="text-gray-400 text-sm">Loading…</div>
          ) : tickets.length === 0 ? (
            <div className="text-gray-400 text-sm">No tickets yet.</div>
          ) : (
            <div className="space-y-4">
              {tickets.map((t) => (
                <div
                  key={t.id}
                  className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl border border-white/10 rounded-2xl p-6"
                  data-testid="support-ticket"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <div className="text-white font-medium">
                        {t.email || `chat ${t.chat_id} (not linked)`}
                      </div>
                      <div className="text-gray-500 text-xs">{formatDate(t.created_at)}</div>
                    </div>
                    <span
                      className={`text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded border ${
                        STATUS_STYLES[t.status] || STATUS_STYLES.open
                      }`}
                    >
                      {t.status}
                    </span>
                  </div>

                  <p className="text-gray-300 whitespace-pre-wrap mb-3">{t.text}</p>

                  {t.replies?.length > 0 && (
                    <div className="border-t border-white/10 pt-3 mt-3 space-y-2">
                      {t.replies.map((r, i) => (
                        <div key={i} className="text-sm">
                          <span className="text-[#f2e2a6]">Reply</span>{' '}
                          <span className="text-gray-500 text-xs">{formatDate(r.at)}</span>
                          <p className="text-gray-300 whitespace-pre-wrap">{r.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminSupport;
